import { useEffect, useState, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useFormik } from "formik";

import {
  Container,
  Grid,
  Box,
  TextareaAutosize,
  Typography,
} from "@material-ui/core";

import { IsMobile } from "utils/is-mobile";

import AutoCompleteField from "components/_refactor/fields/autocomplete";
import FromToDateField from "components/_refactor/fields/from-to-date";
import InputText from "components/_refactor/text/input";
import CurrencyField from "components/_refactor/fields/number";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import Api from "lib/api/httpRequestHelper";

import Checkbox from "./components/checkbox";

import {
  setProductFilters,
  setSelectedProducts,
  updateFormData,
  updateRepresentative,
  updateRepresentativeFilters,
} from "redux/reducers/criarVitrineReducer";

const brandOptions = [
  { value: 1, name: "Karsten" },
  { value: 2, name: "Trussardi" },
  { value: 3, name: "Karsten Decor" },
];

const MOCK_B2B_PRICE_TABLES = [
  { CodTabelaPreco: "555", Descricao: "Lista base" },
  { CodTabelaPreco: "605", Descricao: "Promocional por loja" },
];

function mapPriceTableOption(item) {
  const cod =
    item?.CodTabelaPreco != null ? String(item.CodTabelaPreco).trim() : "";
  const desc = (item?.Descricao ?? item?.Nome ?? "").trim();
  const displayLabel = desc ? `${cod} · ${desc}` : cod;
  return { ...item, CodTabelaPreco: cod, Descricao: desc, displayLabel };
}

function formatDescontoExibicao(pct) {
  if (pct == null || pct === "") return "";
  const n = Number(pct);
  if (Number.isNaN(n)) return String(pct);
  return `${n.toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}%`;
}

function mapPaymentOption(item) {
  const politica = item.Politica.trim() ?? "";
  const desconto = formatDescontoExibicao(item.PercentualDesconto);
  const displayLabel = `${politica} · Desconto: ${desconto}`;
  return { ...item, displayLabel };
}

export default function GeneralData() {
  const dispatch = useDispatch();
  const isMobile = IsMobile();
  const snackbar = useSnackbar();

  const formData = useSelector((state) => state.criarVitrine.formData);

  const [paymentOptions, setPaymentOptions] = useState([]);
  const [loadingPaymentConditions, setLoadingPaymentConditions] =
    useState(false);
  const loadPaymentSeqRef = useRef(0);

  const [priceTableOptions, setPriceTableOptions] = useState([]);
  const [loadingPriceTables, setLoadingPriceTables] = useState(false);
  const loadPriceTableSeqRef = useRef(0);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: formData,
    onSubmit: (values) => {
      dispatch(updateFormData(values));
    },
  });

  const { values, setFieldValue } = formik;

  const recalcMinimumInvoice = useCallback((minimumValue, paymentCondition) => {
    const prazoNum = Number(paymentCondition?.PrazoMedio);
    if (!minimumValue || !prazoNum) return 0;
    return minimumValue / (prazoNum / 30 || 1);
  }, []);

  const handleChange = (key, value) => {
    if (key === "brand") {
      setFieldValue("brand", value);
      setFieldValue("priceTable", null);
      setFieldValue("paymentCondition", null);
      setFieldValue("minimumInvoice", 0);
      dispatch(
        updateFormData({
          brand: value,
          priceTable: null,
          paymentCondition: null,
          minimumInvoice: 0,
        }),
      );
      dispatch(setSelectedProducts([]));
      dispatch(updateRepresentative([]));
      dispatch(setProductFilters({}));
      dispatch(updateRepresentativeFilters({ publicType: "", search: "" }));
      return;
    }

    setFieldValue(key, value);
    dispatch(updateFormData({ [key]: value }));

    if (key === "minimumValue" || key === "paymentCondition") {
      const minimumValue = key === "minimumValue" ? value : values.minimumValue;
      const paymentCondition =
        key === "paymentCondition" ? value : values.paymentCondition;
      const minimumInvoice = recalcMinimumInvoice(
        minimumValue,
        paymentCondition,
      );
      setFieldValue("minimumInvoice", minimumInvoice);
      dispatch(updateFormData({ minimumInvoice }));
    }
  };

  const handleLoadPaymentConditions = useCallback(async (idMarca) => {
    if (!idMarca) {
      setPaymentOptions([]);
      return;
    }

    const seq = ++loadPaymentSeqRef.current;
    setLoadingPaymentConditions(true);

    try {
      const res = await Api.get(
        "list-b2b-payment-conditions-enabled",
        "showcaseB2B",
        `?idMarca=${idMarca}`,
        true,
      );

      if (seq !== loadPaymentSeqRef.current) return;
      if (res?.Success && Array.isArray(res.Result)) {
        setPaymentOptions(res.Result.map(mapPaymentOption));
      } else {
        setPaymentOptions([]);
      }
    } catch (error) {
      if (seq !== loadPaymentSeqRef.current) return;
      setPaymentOptions([]);
      snackbar.showMessage(
        typeof error === "string"
          ? error
          : "Não foi possível carregar as condições de pagamento.",
        { severity: "error" },
      );
    } finally {
      if (seq === loadPaymentSeqRef.current) {
        setLoadingPaymentConditions(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- snackbar do contexto não entra nas deps; loadPaymentSeqRef evita resposta obsoleta
  }, []);

  const handleLoadPriceTables = useCallback(async (idMarca) => {
    if (!idMarca) {
      setPriceTableOptions([]);
      return;
    }

    const seq = ++loadPriceTableSeqRef.current;
    setLoadingPriceTables(true);

    try {
      const res = await Api.get(
        "list-b2b-price-tables-enabled",
        "showcaseB2B",
        `?idMarca=${idMarca}`,
        true,
      );

      if (seq !== loadPriceTableSeqRef.current) return;
      if (res?.Success && Array.isArray(res.Result)) {
        const mapped = res.Result.map(mapPriceTableOption).filter(
          (row) => row.CodTabelaPreco,
        );
        setPriceTableOptions(mapped);
        return;
      }
      setPriceTableOptions([]);
    } catch {
      if (seq !== loadPriceTableSeqRef.current) return;
      setPriceTableOptions(MOCK_B2B_PRICE_TABLES.map(mapPriceTableOption));
    } finally {
      if (seq === loadPriceTableSeqRef.current) {
        setLoadingPriceTables(false);
      }
    }
  }, []);

  useEffect(() => {
    const idMarca = values.brand?.value;
    if (!idMarca) {
      setPaymentOptions([]);
      return;
    }
    handleLoadPaymentConditions(idMarca);
  }, [values.brand?.value, handleLoadPaymentConditions]);

  useEffect(() => {
    const idMarca = values.brand?.value;
    if (!idMarca) {
      setPriceTableOptions([]);
      return;
    }
    handleLoadPriceTables(idMarca);
  }, [values.brand?.value, handleLoadPriceTables]);

  useEffect(() => {
    const prazoNum = Number(values.paymentCondition?.PrazoMedio);
    if (values.minimumValue && prazoNum) {
      const minimumInvoice = values.minimumValue / (prazoNum / 30 || 1) || 0;
      if (minimumInvoice !== values.minimumInvoice) {
        setFieldValue("minimumInvoice", minimumInvoice);
        dispatch(updateFormData({ minimumInvoice }));
      }
    }
  }, [
    dispatch,
    setFieldValue,
    values.paymentCondition,
    values.minimumInvoice,
    values.minimumValue,
  ]);

  return (
    <form onSubmit={formik.handleSubmit}>
      <Container maxWidth="100%" style={{ padding: 0, marginTop: "10px" }}>
        <Grid container spacing={2}>
          <Grid
            item
            xs={12}
            sm={6}
            md={6}
            lg={6}
            xl={4}
            style={{ marginRight: "32px" }}
          >
            <AutoCompleteField
              options={brandOptions}
              label="Marca"
              optionLabelKey="name"
              optionIdKey="value"
              onChange={(option) => handleChange("brand", option)}
              value={formik.values.brand}
              required={true}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={8} xl={6}>
            <Box display="flex">
              <FromToDateField
                label="Período de Vigência"
                disablePrefixLabels={true}
                initialDate={
                  formik.values.untilSoldOut ? "" : formik.values.initialDate
                }
                finalDate={
                  formik.values.untilSoldOut ? "" : formik.values.finalDate
                }
                disabled={formik.values.untilSoldOut}
                setInitialDate={(value) => handleChange("initialDate", value)}
                setFinalDate={(value) => handleChange("finalDate", value)}
                isMobile={isMobile}
                required={!formik.values.untilSoldOut}
                pattern="br"
              />
              <Checkbox
                label="Até finalizar o estoque"
                checked={formik.values.untilSoldOut || false}
                onChange={(event) =>
                  handleChange("untilSoldOut", event.target.checked)
                }
              />
            </Box>
          </Grid>
        </Grid>
        <Grid
          container
          spacing={2}
          alignItems="flex-end"
          style={{ marginTop: "20px" }}
        >
          <Grid item xs={12} sm={12} md={12} lg={4} xl={4}>
            <InputText
              placeholder="Nome da vitrine"
              onChange={(e) => handleChange("name", e.target.value)}
              required={true}
              value={formik.values.name}
              className="form-control"
              style={{ width: "100%" }}
              label="Nome da vitrine"
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
            <AutoCompleteField
              options={paymentOptions}
              label="Condição do Pgto"
              optionLabelKey="displayLabel"
              optionIdKey="IdCondicaoPagamento"
              onChange={(option) => handleChange("paymentCondition", option)}
              value={formik.values.paymentCondition}
              required={true}
              disabled={!values.brand?.value || loadingPaymentConditions}
              loading={loadingPaymentConditions}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={2} xl={2}>
            <CurrencyField
              placeholder="Valor mínimo"
              onChange={(e) => handleChange("minimumValue", e.floatValue || 0)}
              value={formik.values.minimumValue || 0}
              className="form-control"
              style={{ width: "100%" }}
              label="Valor mínimo"
              type="number"
            />
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={2} xl={2}>
            <Box
              display="flex"
              flexWrap="wrap"
              alignItems="center"
              style={{ gap: 8, paddingBottom: "4px" }}
            >
              <Checkbox
                label="Por linha"
                checked={formik.values.byLine || false}
                onChange={(event) => {
                  handleChange("byLine", event.target.checked);
                  handleChange("byOrder", false);
                }}
              />
              <Checkbox
                label="Por pedido"
                checked={formik.values.byOrder || false}
                onChange={(event) => {
                  handleChange("byOrder", event.target.checked);
                  handleChange("byLine", false);
                }}
              />
            </Box>
          </Grid>
        </Grid>
        <Grid
          container
          spacing={2}
          alignItems="flex-start"
          style={{ marginTop: "20px" }}
        >
          <Grid item xs={12} md={6}>
            <Typography variant="h6">Descrição</Typography>
            <TextareaAutosize
              rows={6}
              placeholder="Descrição"
              onChange={(e) => handleChange("description", e.target.value)}
              required={true}
              value={formik.values.description}
              className="form-control"
              style={{ width: "100%", height: "140px" }}
              label="Descrição"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <CurrencyField
                  placeholder="Duplicata mínima"
                  onChange={() => {}}
                  value={formik.values.minimumInvoice || 0}
                  className="form-control"
                  style={{ width: "100%" }}
                  label="Duplicata mínima"
                  type="number"
                  disabled={true}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InputText
                  placeholder="Quantidade mínima por pedido"
                  onChange={(e) =>
                    handleChange(
                      "minimumQuantityPerOrder",
                      Number(e.target.value) || 0,
                    )
                  }
                  value={formik.values.minimumQuantityPerOrder}
                  className="form-control"
                  style={{ width: "100%" }}
                  label="Quantidade mínima por pedido"
                  type="number"
                />
              </Grid>
              <Grid item xs={12}>
                <AutoCompleteField
                  options={priceTableOptions}
                  label="Tabela de Preço"
                  optionLabelKey="displayLabel"
                  optionIdKey="CodTabelaPreco"
                  onChange={(option) => handleChange("priceTable", option)}
                  value={formik.values.priceTable}
                  required={true}
                  disabled={!values.brand?.value || loadingPriceTables}
                  loading={loadingPriceTables}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </form>
  );
}
